import { useRef, useState, useEffect, InputHTMLAttributes } from 'react';
import { ReactGoogleAutocompleteInputProps } from 'react-google-autocomplete';

import { Config } from 'config';
import { Button } from 'components/button';
import {
  InputContainer,
  InputContainerProps,
} from 'components/input-container';

import * as Styles from './styles';
import { loadPlaces, getGoogleMaps, PlacesListener } from './load-places';

const defaultOptions = {
  types: ['geocode'],
  componentRestrictions: { country: 'US' },
  fields: ['address_components', 'formatted_address', 'place_id'],
};

export type AddressAutocompleteInputProps = InputContainerProps &
  ReactGoogleAutocompleteInputProps &
  InputHTMLAttributes<HTMLInputElement>;

export const AddressAutoCompleteInput = ({
  label,
  value,
  style,
  error,
  large,
  footer,
  className,
  onPlaceSelected,
  language = 'en',
  libraries = ['places'],
  googleMapsScriptBaseUrl,
  options = defaultOptions,
  apiKey = Config.googleMapsApiKey,
  inputAutocompleteValue = 'new-password',
  ...props
}: AddressAutocompleteInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const callbackRef = useRef(onPlaceSelected);
  callbackRef.current = onPlaceSelected;
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const optionsKey = JSON.stringify(options);
  const librariesKey = libraries.join(',');

  useEffect(() => {
    let active = true;
    let listener: PlacesListener | undefined;
    setFailed(false);
    loadPlaces(
      apiKey || '',
      language,
      librariesKey.split(','),
      googleMapsScriptBaseUrl
    )
      .then(() => {
        if (!active || !inputRef.current) {
          return;
        }
        const Autocomplete = getGoogleMaps()?.places?.Autocomplete;
        if (!Autocomplete) {
          throw new Error('Address suggestions unavailable');
        }
        const autocomplete = new Autocomplete(inputRef.current, {
          ...defaultOptions,
          ...JSON.parse(optionsKey),
        });
        listener = autocomplete.addListener('place_changed', () => {
          callbackRef.current?.(
            autocomplete.getPlace(),
            inputRef as React.RefObject<HTMLInputElement>,
            { current: autocomplete }
          );
        });
      })
      .catch(() => {
        if (active) {
          setFailed(true);
        }
      });
    return () => {
      active = false;
      listener?.remove();
    };
  }, [
    apiKey,
    language,
    librariesKey,
    googleMapsScriptBaseUrl,
    optionsKey,
    attempt,
  ]);

  return (
    <InputContainer
      label={label}
      style={style}
      value={value}
      error={error}
      large={large}
      className={className}
      footer={
        failed ? (
          <>
            <span role="status">
              Address suggestions unavailable. Enter the address manually.
            </span>
            <Button
              text="Retry suggestions"
              onClick={() => setAttempt((value) => value + 1)}
            />
          </>
        ) : (
          footer
        )
      }
    >
      <Styles.Input
        id={label}
        error={error}
        value={value}
        ref={inputRef}
        autoComplete={inputAutocompleteValue}
        {...props}
      />
    </InputContainer>
  );
};
